import { useTranslation } from 'react-i18next';
import BackButton from '~/components/BackButton';
import DateTime from '~/components/DateTime';
import Money from '~/components/Money';
import PageHeader from '~/components/PageHeader';
import Button from '~/components/ui/Button';
import { InnerCard } from '~/components/ui/Card';
import Icon from '~/components/ui/Icon';
import { SkeletonCard } from '~/components/ui/Skeleton';
import Stack from '~/components/ui/Stack';
import { cn } from '~/lib/utils';
import useAllTransactions from '~/modules/subscription/useAllTransactions';
import { statusStyles } from '~/modules/subscription/utils';

function TransactionDetail({
  title,
  value,
  classNameValue,
}: {
  title: string;
  value: React.ReactNode;
  classNameValue?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs text-muted-blue">{title}</p>
      <div className={cn('text-sm', classNameValue)}>{value}</div>
    </div>
  );
}

export default function Transactions() {
  const { t } = useTranslation('', { keyPrefix: 'subscription.transactions' });
  const {
    transactions,
    totalPages,
    currentPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
  } = useAllTransactions();

  return (
    <Stack col gap={6} className="flex-1">
      <BackButton to="/subscription" />
      <PageHeader id="paymentHistory" />
      <div className="flex flex-col gap-2.5">
        <p className="text-base font-bold">{t('title')}</p>
        {(isLoading || isFetching) &&
          Array.from({ length: 10 }).map((_, index) => <SkeletonCard height={90} key={index} />)}
        {transactions.map((item) => (
          <InnerCard key={item.id}>
            <div className="grid grid-cols-3 gap-2.5">
              <TransactionDetail title={t('details.date')} value={<DateTime value={item.date} />} />
              <TransactionDetail
                title={t('details.amount')}
                value={<Money value={item.amount} />}
              />
              <TransactionDetail
                title={t('details.status')}
                value={item.status}
                classNameValue={cn(
                  statusStyles[item.status],
                  'py-1 px-2 rounded-sm capitalize w-max',
                )}
              />
            </div>
          </InnerCard>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-between items-center gap-2.5 w-full">
          <Button
            onPress={() => fetchPreviousPage()}
            isDisabled={!hasPreviousPage || isFetchingPreviousPage}
            variant="outlined"
            size="inherit"
            className="py-1.5 px-4"
          >
            <Icon name="chevron" className="rotate-180 text-[19px]" />
          </Button>

          <span className="text-sm">
            {t('pagination.page', { current: currentPage, total: totalPages })}
          </span>

          <Button
            onPress={() => fetchNextPage()}
            isDisabled={!hasNextPage || isFetchingNextPage}
            variant="outlined"
            size="inherit"
            className="py-1.5 px-4"
          >
            <Icon name="chevron" className="text-[19px]" />
          </Button>
        </div>
      )}
    </Stack>
  );
}
